import React, { useEffect, useRef } from 'react';
import { 
  ShieldCheck, 
  Target, 
  Database, 
  Terminal, 
  Layers, 
  TrendingUp, 
  Command, 
  FileText, 
  Cpu, 
  Fingerprint, 
  Network, 
  Workflow, 
  Lock, 
  Globe,
  MapPin,
  Zap,
  Search,
  Scale,
  Users,
  Radar,
  Check,
  BrainCircuit,
  ArrowRight,
  Mail,
  Mic2,
  Link as LinkIcon
} from 'lucide-react';

const NeuralMeshLight = ({ opacity = 0.15, color = "99, 102, 241" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const resizeAndDraw = () => {
      if (canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const nodes = [];
      const nodeCount = 45;
      
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: (Math.sin(i * 123) * 0.5 + 0.5) * canvas.width,
          y: (Math.cos(i * 321) * 0.5 + 0.5) * canvas.height,
          size: (i % 3) * 0.5 + 0.8
        });
      }

      nodes.forEach(n => {
        ctx.fillStyle = `rgba(${color}, ${opacity})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.strokeStyle = `rgba(${color}, ${opacity * 0.4})`;
      ctx.lineWidth = 0.5;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
    };
    
    resizeAndDraw();
    window.addEventListener('resize', resizeAndDraw);
    return () => window.removeEventListener('resize', resizeAndDraw);
  }, [opacity, color]);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />;
};

export default function FrontierAgenticBrief(){

  const engagementModules = [
    { 
      id: "01", 
      badge: "BOARDROOM & IC",
      title: "Sector Thesis & Moats",
      target: "GPs, MANAGING DIRECTORS, C-SUITE",
      color: "cyan", 
      text: "Live stress-testing of current portfolio assets, value-creation plans, and new deal theses against proprietary Sector Brains. We identify un-replicable structural & intelligence moats and shift underwriting toward true AI value drivers.",
      chipsTitle: "ACTIVE SECTOR COVERAGES",
      chips: ["Defense", "Logistics", "HCLS & Bio-Digital", "Cyber", "FinTech", "Retail", "Telco", "Energy", "+ More"],
      deliverables: ["MOAT DIAGNOSTIC", "GROWTH THESES", "ALPHA SEGMENTS"],
      outcomeLabel: "ASSET HANDOVER:",
      outcome: "Custom Sector Brain mapping your thesis, sector moats, and asset positioning."
    },
    { 
      id: "02", 
      badge: "DEAL DESKS & EXECUTION",
      title: "Deal & Strategy Workflows",
      target: "DEAL TEAMS, CORP DEV & M&A LEADS",
      color: "indigo", 
      text: "Hands-on demonstration of human-in-the-loop agentic research and strategic deal workflows. We show how deal teams scale research velocity by 10x while maintaining institutional rigor and auditability.",
      chipsTitle: "PE, IB & STRATEGY PIPELINE",
      chips: ["Target Sourcing", "Forensic CDD+", "Value Creation", "Final Verdicts"],
      deliverables: ["HIGH-CONVICTION MEMOS", "WORKFLOW CO-DESIGN", "DEAL ORCHESTRATION"],
      outcomeLabel: "PLATFORM HANDOVER:",
      outcome: "Live access to Frontier's proprietary deal-engine workflows and agentic protocols."
    },
    { 
      id: "03", 
      badge: "TECH & AI LABS",
      title: "Private Brain Architecture",
      target: "CTOS, AI LAB LEADS & ARCHITECTS",
      color: "purple", 
      text: "Deep-dive into Context-Locked Agent Foundry architecture, Institutional DNA, and zero-trust security. We provide an auditable spec to deploy a sovereign Private Brain inside your cloud perimeter.",
      chipsTitle: "ARCHITECTURE STACK",
      chips: ["[ INVEST DNA ]", "[ HEURISTICS ]", "[ MEMORY ]", "[ PRIVATE VPC ]", "CORE AI MODEL"],
      deliverables: ["PRIVATE BRAIN", "KNOWLEDGE ORCHESTRATION", "SWARM ORCHESTRATION"],
      outcomeLabel: "TECHNICAL HANDOVER:",
      outcome: "Sovereign Architecture Spec to build your Private Brain inside your cloud perimeter."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800 font-sans p-2 md:p-4 lg:p-6 flex flex-col items-center justify-center print:bg-slate-50 print:p-0">
      
      {/* SINGLE CANVAS CONTAINER */}
      <div className="w-full max-w-[1440px] bg-slate-50 border border-slate-200 rounded-3xl overflow-hidden shadow-[0_20px_50px_rgba(8,_112,_184,_0.07)] relative z-10 flex flex-col print:border-none print:shadow-none min-h-[720px] print:min-h-[100vh]">
        {/* Subtle light mesh using a sophisticated indigo/pink blend */}
        <NeuralMeshLight opacity={0.12} color="79, 70, 229" /> 
        
        <div className="p-6 md:p-8 lg:p-10 relative z-10 flex-1 flex flex-col">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8 border-b border-slate-200 pb-6 shrink-0">
             <div className="flex items-center gap-4">
                <div>
                  <a href="https://strategyandagents.com/" target="_blank" rel="noopener noreferrer" className="text-xl md:text-2xl font-black uppercase tracking-[0.3em] flex items-center hover:opacity-80 transition-opacity">
                    <span className="text-slate-900">KR</span>
                    <span className="text-cyan-600 mx-1">-</span>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-indigo-600">FRONTIER</span>
                  </a>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1.5">Frontier AI for High-Stakes Capital & Corporate Strategy.</div>
                </div>
             </div>
             <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-50 border border-cyan-100 text-[10px] font-black uppercase tracking-widest text-cyan-700">
                <ShieldCheck size={14} className="text-cyan-600" /> Confidential // PROPRIETARY IP
             </div>
          </div>

          {/* =======================================================
              BLOCK 1: THE SOVEREIGN CORE (Lifecycle + Assembly)
              ======================================================= */}
          <div className="flex flex-col mb-10 flex-1">

             <h2 className="text-4xl md:text-5xl lg:text-6xl text-center font-black text-slate-900 tracking-tighter mb-4 mx-auto uppercase">
               ESTABLISHING <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-cyan-600">INSTITUTIONAL AI CONVICTION.</span>
             </h2>
             <p className="text-slate-600 text-sm md:text-base text-center leading-relaxed font-medium mb-10 max-w-6xl mx-auto">
               Hands-on advisory mandates that equip Board Members, Investment Committees, and Chief AI Officers to command AI disruption. Through closed-door forums, masterclasses, or forensic AI audits, we help senior leadership stress-test portfolio assets, reinvent deal workflows, and map a sovereign AI roadmap before committing capital.
             </p>

             <div className="flex items-center gap-3 mb-3 justify-center">
               <div className="w-12 h-px bg-slate-300"></div>
               <span className="text-[10px] font-black uppercase tracking-[0.3em] text-pink-600 text-center">STRATEGIC IMMERSION // LEADERSHIP READINESS</span>
               <div className="w-12 h-px bg-slate-300"></div>
             </div>

             <div className="flex justify-center mb-8 relative z-10">
                <div className="px-3 py-1.5 rounded border border-slate-200 bg-slate-50 text-[8.5px] font-black uppercase tracking-widest text-slate-500 shadow-sm flex items-center gap-2">
                  <Layers size={12} className="text-slate-400" />
                  DEPLOY AS STANDALONE MODULES OR BESPOKE COMBINATIONS
                </div>
             </div>

             {}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 relative z-10">
                {engagementModules.map((module) => (
                   <div key={module.id} className="flex flex-col relative group h-full">
                      <div className="bg-white border border-slate-200 p-6 w-full rounded-[24px] relative overflow-hidden flex flex-col items-start text-left shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:border-slate-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all h-full">
                         
                         {/* Dynamic Background Watermarks */}
                         <div className={`absolute -right-12 -bottom-12 w-64 h-64 opacity-[0.04] group-hover:opacity-[0.08] transition-opacity duration-700 pointer-events-none text-${module.color}-600`}>
                            {module.id === "01" && <ShieldCheck size={280} />}
                            {module.id === "02" && <Workflow size={280} />}
                            {module.id === "03" && <Database size={280} />}
                         </div>
                         
                         <div className={`absolute top-0 right-0 w-32 h-32 bg-${module.color}-100 rounded-full blur-3xl pointer-events-none opacity-50`}></div>
                         
                         {/* Badge & Title */}
                         <div className="flex items-center justify-between w-full mb-3 relative z-10">
                            <div className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest shadow-sm bg-${module.color}-50 border border-${module.color}-100 text-${module.color}-700`}>
                               {module.id} // {module.badge}
                            </div>
                         </div>

                         <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-1.5 relative z-10">{module.title}</h3>
                         <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-4 relative z-10">
                           TARGET: {module.target}
                         </p>
                         
                         <p className="text-[11px] text-slate-600 leading-relaxed font-medium mb-6 relative z-10 border-b border-slate-100 pb-5">
                           {module.text}
                         </p>
                         
                         {/* Chips / Process Flow Section */}
                         <div className="w-full mb-6 relative z-10">
                            <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                               <Radar size={10} className={`text-${module.color}-600`} /> {module.chipsTitle}
                            </div>
                            <div className="flex flex-wrap gap-2">
                               {module.chips.map((chip, i) => (
                                  <span key={i} className="px-2.5 py-1 rounded bg-slate-50 border border-slate-200 text-[9px] font-bold text-slate-700 shadow-sm whitespace-nowrap">
                                     {chip}
                                  </span>
                               ))}
                            </div>
                         </div>

                         {/* Deliverable Tags Block */}
                         <div className="mt-auto w-full pt-4 relative z-10">
                            <div className="flex flex-wrap gap-2 mb-4">
                               {module.deliverables.map((tag, i) => (
                                 <span key={i} className={`px-2 py-0.5 rounded border border-${module.color}-200 bg-${module.color}-50 text-[7px] font-black uppercase tracking-widest text-${module.color}-700`}>
                                   {tag}
                                 </span>
                               ))}
                            </div>
                            <div className="flex flex-col">
                               <span className="text-[9px] font-black uppercase tracking-widest text-slate-900 mb-1">{module.outcomeLabel}</span>
                               <span className="text-[10px] text-slate-600 font-medium leading-relaxed">{module.outcome}</span>
                            </div>
                         </div>
                      </div>
                   </div>
                ))}
             </div>

             {}
             {/* The Unified Assembly Card (Strategy + Tech) */}
             <div className="bg-slate-50 border border-slate-200 rounded-[24px] p-6 lg:p-8 relative overflow-hidden flex flex-col z-10 shadow-sm">
                <div className="absolute top-0 left-0 w-[800px] h-full bg-gradient-to-r from-pink-50 via-indigo-50 to-transparent pointer-events-none opacity-50"></div>
                
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5 relative z-10">
                   <div className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800">
                      GLOBAL DOMAIN LEADS // CROSS-BORDER SECTOR & AI STRATEGISTS
                   </div>
                   <div className="text-[8px] font-black uppercase tracking-widest text-cyan-700 border border-cyan-200 bg-cyan-50 px-3 py-1 rounded-md shadow-sm">
                      BOARDROOM STRATEGY & AI EXPERTS
                   </div>
                </div>
                
                {/* 6-Grid for Principals */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 relative z-10 mb-6">
                   
                   {/* Principal 1: Bharat */}
                   <div className="flex flex-col group">
                      <div className="flex items-center gap-3 mb-2.5">
                         <img src="https://media.licdn.com/dms/image/v2/D4E03AQEWXiBY6cV1cQ/profile-displayphoto-crop_800_800/B4EZ0SF9RBIUAI-/0/1774125046033?e=1787184000&v=beta&t=BDReVLZDt3LRLw9iBoVlYlDZtOczaW8Rr_7ULVPzxIQ" alt="Dr. Bharat Madan" className="w-10 h-10 rounded-full border border-slate-300 object-cover shadow-sm shrink-0 group-hover:border-cyan-500 transition-colors duration-300 bg-white" />
                         <div>
                            <h4 className="text-slate-900 font-bold text-[11px] mb-0.5 whitespace-nowrap">Dr. Bharat Madan</h4>
                            <div className="flex items-center gap-1 mb-0.5">
                               <MapPin size={8} className="text-slate-500" />
                               <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">Luxembourg</span>
                            </div>
                            <div className="text-[7.5px] font-bold uppercase tracking-widest text-cyan-600">Boardroom & IC Lead</div>
                         </div>
                      </div>
                   </div>

                   {/* Principal 2: Thomas */}
                   <div className="flex flex-col group lg:pl-5 lg:border-l border-slate-200">
                      <div className="flex items-center gap-3 mb-2.5">
                         <img src="https://media.licdn.com/dms/image/v2/D4D03AQE-PqwBQ8VdBQ/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1638770535624?e=1787184000&v=beta&t=NChANp_musBek3y9NqPacemtyV1xRHGTPILLIQn3avc" alt="Thomas Kipp" className="w-10 h-10 rounded-full border border-slate-300 object-cover shadow-sm shrink-0 group-hover:border-cyan-500 transition-colors duration-300 bg-white" />
                         <div>
                            <h4 className="text-slate-900 font-bold text-[11px] mb-0.5 whitespace-nowrap">Thomas Kipp</h4>
                            <div className="flex items-center gap-1 mb-0.5">
                               <MapPin size={8} className="text-slate-500" />
                               <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">Dubai</span>
                            </div>
                            <div className="text-[7.5px] font-bold uppercase tracking-widest text-cyan-600">Sector Brains Lead</div>
                         </div>
                      </div>
                   </div>

                   {/* Principal 3: Balbinder */}
                   <div className="flex flex-col group lg:pl-5 lg:border-l border-slate-200">
                      <div className="flex items-center gap-3 mb-2.5">
                         <img src="https://media.licdn.com/dms/image/v2/D4D03AQH9AJJ2wiz6WA/profile-displayphoto-crop_800_800/B4DZruW6OgHwAI-/0/1764935577241?e=1787184000&v=beta&t=uiQo9TnjvbKpplNQeTK5xIVAwWxCWn2CrBTxOSHmPXo" alt="Balbinder Bhatia" className="w-10 h-10 rounded-full border border-slate-300 object-cover shadow-sm shrink-0 group-hover:border-indigo-500 transition-colors duration-300" />
                         <div>
                            <h4 className="text-slate-900 font-bold text-[11px] mb-0.5 whitespace-nowrap">Balbinder Bhatia</h4>
                            <div className="flex items-center gap-1 mb-0.5">
                               <MapPin size={8} className="text-slate-500" />
                               <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">Abu Dhabi</span>
                            </div>
                            <div className="text-[7.5px] font-bold uppercase tracking-widest text-indigo-600">Deal Engine Architect</div>
                         </div>
                      </div>
                   </div>

                   {/* Principal 4: Maheshwar */}
                   <div className="flex flex-col group lg:pl-5 lg:border-l border-slate-200">
                      <div className="flex items-center gap-3 mb-2.5">
                         <img src="https://media.licdn.com/dms/image/v2/D5603AQHSt29zIt-Ywg/profile-displayphoto-crop_800_800/B56Zwkk_eyIwAI-/0/1770140204184?e=1787184000&v=beta&t=LSK4pIsFJeWm0HQ2ZmgWwMBr21yraAp1dVMBk2WI_4c" alt="Maheshwar D." className="w-10 h-10 rounded-full border border-slate-300 object-cover shadow-sm shrink-0 group-hover:border-indigo-500 transition-colors duration-300" />
                         <div>
                            <h4 className="text-slate-900 font-bold text-[11px] mb-0.5 whitespace-nowrap">Maheshwar D.</h4>
                            <div className="flex items-center gap-1 mb-0.5">
                               <MapPin size={8} className="text-slate-500" />
                               <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">Bengaluru</span>
                            </div>
                            <div className="text-[7.5px] font-bold uppercase tracking-widest text-indigo-600">M&A Orchestration</div>
                         </div>
                      </div>
                   </div>

                   {/* Principal 5: Zuhaib */}
                   <div className="flex flex-col group lg:pl-5 lg:border-l border-slate-200">
                      <div className="flex items-center gap-3 mb-2.5">
                         <img src="https://media.licdn.com/dms/image/v2/D5603AQGUcHRzPGPxvA/profile-displayphoto-crop_800_800/B56ZkHcCh6IAAM-/0/1756766415796?e=1787184000&v=beta&t=aABg_e345EZ9mIAIJ1mJ_qLOf3eHKC4BG4cIfNGV-nM" alt="Zuhaib Ahmed" className="w-10 h-10 rounded-full border border-slate-300 object-cover shadow-sm shrink-0 group-hover:border-purple-500 transition-colors duration-300" />
                         <div>
                            <h4 className="text-slate-900 font-bold text-[11px] mb-0.5 whitespace-nowrap">Zuhaib Ahmed</h4>
                            <div className="flex items-center gap-1 mb-0.5">
                               <MapPin size={8} className="text-slate-500" />
                               <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">Dubai</span>
                            </div>
                            <div className="text-[7.5px] font-bold uppercase tracking-widest text-purple-600">Context Foundry Lead</div>
                         </div>
                      </div>
                   </div>

                   {/* Principal 6: Sanjay */}
                   <div className="flex flex-col group lg:pl-5 lg:border-l border-slate-200">
                      <div className="flex items-center gap-3 mb-2.5">
                         <img src="https://media.licdn.com/dms/image/v2/D5603AQEe-JhQnFtR1A/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1718228856300?e=1787184000&v=beta&t=IwjgdR5L_0SLXmUlgvMXVVrVQ3ed1jdSxviQ_zMpjEc" alt="Sanjay Cherian" className="w-10 h-10 rounded-full border border-slate-300 object-cover shadow-sm shrink-0 group-hover:border-purple-500 transition-colors duration-300" />
                         <div>
                            <h4 className="text-slate-900 font-bold text-[11px] mb-0.5 whitespace-nowrap">Sanjay Cherian</h4>
                            <div className="flex items-center gap-1 mb-0.5">
                               <MapPin size={8} className="text-slate-500" />
                               <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">Austin</span>
                            </div>
                            <div className="text-[7.5px] font-bold uppercase tracking-widest text-purple-600">Sovereign Memory</div>
                         </div>
                      </div>
                   </div>
                </div>

                {/* Subtext Bar */}
                <div className="pt-5 border-t border-slate-200 w-full flex flex-col items-center relative z-10">
                   <div className="flex flex-wrap justify-center gap-2 lg:gap-4">
                      {['Logistics AI C-Suite Thesis', 'Sovereign Defense (Korea Forum)', 'Claude Cowork for PE', 'Smart Deal Team Workflows', 'Omnicontext Private Brain', '100% Fee Credit Guarantee'].map((s, i) => (
                        <span key={i} className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-[9px] font-bold uppercase tracking-widest text-slate-600 shadow-sm flex items-center gap-1.5">
                          {i === 5 ? <Check size={12} className="text-pink-600" /> : <Command size={10} className="text-slate-400" />} {s}
                        </span>
                      ))}
                   </div>
                </div>
             </div>
          </div>

          {}
          {/* =======================================================
              BLOCK 2: EXECUTION MODALITIES & COMMERCIAL GATEWAY
              ======================================================= */}
          <div className="shrink-0 border-t border-slate-200 pt-8 mt-auto relative z-10">
             
             <div className="flex flex-col lg:flex-row gap-8 items-stretch h-full">
                
                {/* Left Side: Delivery Formats */}
                <div className="flex-1 flex flex-col justify-center">
                   <div className="flex items-center gap-3 mb-4">
                      <span className="text-[10px] font-black uppercase tracking-[0.3em] text-pink-600 bg-pink-50 px-3 py-1 rounded-full border border-pink-100 shadow-sm shrink-0">DELIVERABLES & ENGAGEMENT MODEL</span>
                   </div>
                   
                   <h3 className="text-[17px] md:text-xl font-black text-slate-900 tracking-tight mb-3">STRATEGIC BRIEFINGS ➔ DIAGNOSTIC BLUEPRINTS</h3>
                   <p className="text-sm text-slate-600 leading-relaxed font-medium max-w-2xl mb-8">
                      From stage keynotes to forensic technical deep-dives, our entry engagements deliver concrete <strong>Strategic Clarity</strong> and organizational alignment before major capital deployment.
                   </p>

                   {/* Format Chips Grid */}
                   <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { title: 'SUMMITS & KEYNOTES', icon: <Mic2 size={18} className="mb-1.5 text-cyan-600" /> },
                        { title: 'BOARDROOM FORUMS', icon: <Users size={18} className="mb-1.5 text-indigo-600" /> },
                        { title: 'ALIGNMENT SPRINTS', icon: <TrendingUp size={18} className="mb-1.5 text-purple-600" /> },
                        { title: 'MOAT DIAGNOSTICS', icon: <Search size={18} className="mb-1.5 text-pink-600" /> }
                      ].map((format, i) => (
                        <div key={i} className="bg-white border border-slate-200 hover:border-slate-300 transition-colors rounded-xl p-2.5 py-3 flex flex-col items-center justify-center text-center group cursor-pointer shadow-sm hover:shadow-md">
                           {format.icon}
                           <span className="text-[10px] leading-tight font-black uppercase tracking-wider text-slate-600 group-hover:text-slate-900 transition-colors">{format.title}</span>
                        </div>
                      ))}
                   </div>
                </div>

                {/* Right Side: Commercial Gateway */}
                <div className="lg:w-[420px] shrink-0 bg-white border border-slate-200 rounded-[28px] p-8 flex flex-col justify-center relative overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.06)]">
                   {/* Background Glow */}
                   <div className="absolute top-0 right-0 w-full h-full bg-pink-50 blur-3xl pointer-events-none opacity-60"></div>

                   <div className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-500 mb-4 text-center relative z-10">
                      ADVISORY & IMMERSION MANDATE
                   </div>
                   
                   <div className="flex flex-col items-center justify-center border-b border-slate-100 pb-5 mb-5 relative z-10">
                      <div className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tighter mb-1.5">
                         $25k
                      </div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-600 bg-slate-50 border border-slate-200 px-3 py-1 rounded-full shadow-sm">
                         FLAT INVESTMENT // ZERO SEAT FEES
                      </div>
                   </div>

                   <div className="flex flex-col items-center text-center relative z-10">
                      <div className="flex items-center justify-center gap-2 text-pink-700 mb-3 bg-pink-50 px-4 py-1.5 rounded-lg border border-pink-100 shadow-sm w-full">
                         <Zap size={14} className="animate-pulse" />
                         <span className="text-[10px] font-black uppercase tracking-widest">100% CREDITED TOWARD FULL DEPLOYMENT</span>
                      </div>
                      <p className="text-[11px] text-slate-600 font-medium leading-relaxed">
                         100% of the immersion fee is credited directly toward full engine deployment or strategic verdict mandates signed within 60 days.
                      </p>
                   </div>

                   <button className="mt-6 w-full bg-slate-900 hover:bg-slate-800 text-white font-black text-[11px] uppercase tracking-widest py-3.5 rounded-xl shadow-[0_8px_20px_rgba(15,23,42,0.2)] transition-all flex items-center justify-center gap-3 group relative z-10">
                      COMMISSION EXECUTIVE IMMERSION <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                   </button>
                </div>

             </div>
          </div>

          {}
          {/* PAGE FOOTER (Minimalist One-Line Registry) */}
          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between w-full shrink-0 gap-4 relative z-10">
             
             {/* Left: Branding & City Nodes */}
             <div className="flex flex-col gap-1.5 shrink-0">
                <a href="https://strategyandagents.com/" target="_blank" rel="noopener noreferrer" className="text-2xl font-black uppercase tracking-[0.3em] flex items-center whitespace-nowrap hover:opacity-80 transition-opacity">
                   <span className="text-slate-900">KR</span>
                   <span className="text-cyan-600 mx-1">-</span>
                   <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-indigo-600">FRONTIER</span>
                </a>
                <div className="flex flex-wrap items-center gap-2 md:gap-2.5 text-[7px] font-black uppercase tracking-[0.3em] text-slate-500">
                   <span className="text-slate-800">Abu Dhabi (HQ)</span>
                   <span className="text-slate-300">|</span>
                   <span>New York</span>
                   <span className="text-slate-300">|</span>
                   <span>Luxembourg</span>
                   <span className="text-slate-300">|</span>
                   <span>Bengaluru</span>
                </div>
             </div>

             {/* Right: Detailed Contacts */}
             <div className="flex items-center gap-8 shrink-0">
                <div className="flex items-center gap-8 text-right hidden md:flex">
                   <div>
                      <div className="text-[7px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Principal Contact</div>
                      <div className="text-[10px] font-black text-slate-800">anuj@knowledgeridge.com</div>
                   </div>
                   <div>
                      <div className="text-[7px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Digital Command</div>
                      <div className="text-[10px] font-black text-slate-800 underline decoration-pink-300 underline-offset-4">www.strategyandagents.com</div>
                   </div>
                </div>
             </div>
          </div>

        </div>
      </div>

    </div>
  );
};
